"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PLAN_QUOTAS, type PlanTier } from "@/lib/authz/plans";
import {
  changeSubscriptionPlan,
  previewSubscriptionPlanChange,
  type ChangePlanQuote,
} from "@/lib/billing/changeSubscriptionPlan";
import { openCustomerPortal } from "@/lib/billing/customerPortal";
import { closeCheckoutTab, navigateCheckoutTab, openCheckoutTab } from "@/lib/billing/checkoutTab";
import { formatMoney } from "@/lib/billing/money";

// Coded failures that mean "this subscription needs the portal instead" — the
// copy just explains why before we offer it. With the checkout-based flow, no
// money ever moves in this dialog (payment happens in Shopify checkout), so
// every failure here is safe to retry.
const FAILURE_COPY: Record<string, string> = {
  legacy_subscription: "Your subscription is from our previous catalog, so plan changes happen in the billing portal.",
  multiple_subscriptions: "You have more than one active subscription, so plan changes happen in the billing portal.",
  subscription_email_mismatch: "Your subscription is registered under a different email, so plan changes happen in the billing portal.",
  subscription_not_active: "Your subscription isn't in good standing — sort it out in the billing portal first.",
  no_subscription: "We couldn't find an active subscription for this account.",
  unsupported_currency: "We couldn't price this plan in your billing currency.",
  unsupported_interval: "Your billing schedule isn't supported for in-app changes yet.",
  unsupported_items: "Your subscription items need a manual change.",
  unsupported_tier: "That plan isn't available for in-app changes.",
  downgrade_unavailable: "In-app downgrades aren't available — use the billing portal to lower your plan.",
  already_on_plan: "You're already on this plan.",
  plan_change_unavailable: "In-app plan changes aren't available yet.",
  plan_change_unavailable_now: "Plan changes are temporarily unavailable. Try again shortly.",
  plan_change_in_progress: "A plan change is already in progress — give it a moment.",
  plan_change_pending_review: "A previous plan change didn't finish cleanly. Contact support before trying again.",
  seal_api_failed: "Our billing provider had a hiccup. Nothing was charged — try again in a moment.",
  credit_unavailable: "We couldn't apply your unused-time credit. Nothing was charged — try again.",
  request_failed: "Something went wrong starting the upgrade. Nothing was charged — try again.",
};

export type ChangePlanDialogProps = {
  /** Target tier, or null when closed. */
  targetTier: PlanTier | null;
  targetName: string;
  currentPlan: PlanTier;
  /** Localized display price line shown ONLY while the server quote loads (best-effort). */
  priceLine: string | null;
  onClose: () => void;
  /** Called after a successful change so the caller can refresh entitlements. */
  onChanged: () => Promise<void> | void;
};

export function ChangePlanDialog({
  targetTier,
  targetName,
  currentPlan,
  priceLine,
  onClose,
}: ChangePlanDialogProps) {
  const [pending, setPending] = useState(false);
  // The server quote carries the REAL billing interval and catalog price for
  // the subscription's currency. The confirm button stays disabled until it
  // arrives; Shopify checkout itself presents the final amount before payment.
  const [quote, setQuote] = useState<ChangePlanQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  useEffect(() => {
    setQuote(null);
    setQuoteError(null);
    if (targetTier === null) return;
    let cancelled = false;
    void previewSubscriptionPlanChange(targetTier).then((result) => {
      if (cancelled) return;
      if (result.ok === true) setQuote(result);
      else setQuoteError(result.code);
    });
    return () => {
      cancelled = true;
    };
  }, [targetTier]);

  const confirm = async () => {
    if (!targetTier || pending || quote === null) return;
    setPending(true);
    // Open the checkout tab synchronously (inside the click gesture) so popup
    // blockers allow it; navigate it once the URL resolves.
    const checkoutTab = openCheckoutTab();
    const result = await changeSubscriptionPlan(targetTier);

    // `=== true` (not truthiness) — this repo compiles with strictNullChecks
    // off, where only equality checks narrow a discriminated union.
    if (result.ok === true) {
      // Checkout opens in a NEW tab; the app stays put. The old plan is
      // cancelled automatically (server-side) after checkout completes, and the
      // app refreshes when the user returns to this tab.
      navigateCheckoutTab(checkoutTab, result.checkoutUrl);
      setPending(false);
      onClose();
      return;
    }

    closeCheckoutTab(checkoutTab);
    setPending(false);
    const copy = FAILURE_COPY[result.code] ?? "We couldn't start the upgrade just now.";
    toast.error(copy, {
      action: {
        label: "Open billing portal",
        onClick: () => openCustomerPortal(),
      },
      duration: 10000,
    });
    onClose();
  };

  const quotePrice = quote !== null ? formatMoney(quote.price) : null;
  const quoteInterval = quote !== null ? (quote.billing === "annual" ? "year" : "month") : null;
  const quoteCredit =
    quote !== null && quote.prorated === true && quote.unusedCredit > 0
      ? formatMoney({ amount: String(quote.unusedCredit), currencyCode: quote.price.currencyCode })
      : null;
  const quoteFirstPayment =
    quote !== null && quoteCredit !== null
      ? formatMoney({ amount: String(quote.estimatedFirstPayment), currencyCode: quote.price.currencyCode })
      : null;

  return (
    <Dialog open={targetTier !== null} onOpenChange={(open) => (!open && !pending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {PLAN_QUOTAS[currentPlan].label}
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            {targetName}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 pt-2 text-sm leading-6">
              {quote !== null ? (
                <>
                  <p>
                    Upgrading opens a secure Shopify checkout for <strong>{targetName}</strong> at{" "}
                    <strong>
                      {quotePrice ?? "the plan price"} per {quoteInterval}
                    </strong>
                    . You pay there — nothing is charged until you complete checkout.
                  </p>
                  {quoteCredit !== null ? (
                    <p>
                      A <strong>{quoteCredit} credit</strong> for the unused time on your current plan
                      is already applied — your first payment is about{" "}
                      <strong>{quoteFirstPayment ?? "the reduced amount"}</strong>, then{" "}
                      {quotePrice ?? "full price"} per {quoteInterval} from the next cycle.
                    </p>
                  ) : null}
                  <p>
                    Once your payment goes through, your current{" "}
                    <strong>{PLAN_QUOTAS[currentPlan].label}</strong> subscription is cancelled
                    automatically — it won&rsquo;t bill again.
                    {quoteCredit === null ? " Unused time on it isn't credited." : ""}
                  </p>
                  <p>Your new tools and AI token allowance unlock right after checkout.</p>
                </>
              ) : quoteError !== null ? (
                <p>{FAILURE_COPY[quoteError] ?? "We couldn't check your subscription just now."}</p>
              ) : (
                <p>
                  {priceLine ? `${priceLine} ` : ""}
                  Checking your subscription…
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" className="rounded-full" disabled={pending} onClick={onClose}>
            Keep current plan
          </Button>
          {quoteError !== null ? (
            <Button className="rounded-full" onClick={() => openCustomerPortal()}>
              Open billing portal
            </Button>
          ) : (
            <Button
              className="rounded-full"
              disabled={pending || quote === null}
              onClick={() => void confirm()}
            >
              {pending ? "Opening checkout…" : quote === null ? "Checking…" : "Continue to checkout"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
